import React from 'react';

import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import useUserHook from 'hooks/useUserHook';

import { PageNotFound } from 'components/common/ErrorPages/pageNotFound';
import GuardedRoute from 'components/common/GuardedRoute/guardedRoute';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import NoAccess from 'components/common/NoAccess/noAccess';
import { SideMenuListProps } from 'components/common/SideMenu/SideMenu';

import { RedirectToLast } from 'components/Dashboards/Program/ProgramWrapper/redirectToLast';
import { RenderProjectDataStories } from 'components/Dashboards/Project/Components/RenderProjectDataStories';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Responsive } from 'components/Dashboards/responsive';
import { CompletionPlanPage } from 'components/pages/CompletionPlanPage';
import { EditBulkActionRulesPage } from 'components/pages/EditBulkActionRulesPage';
import { MyFocusPage } from 'components/pages/MyFocusPage';
import { PinboardsPage } from 'components/pages/PinboardsPage';
import { ProgressPage } from 'components/pages/ProgressPage';
import { ProjectActivitiesPage } from 'components/pages/ProjectActivitiesPage';
import { ProjectStatusPage } from 'components/pages/ProjectStatusPage';

export const sideMenu: SideMenuListProps[] = [
    {
        ...executionConfig.myFocusPage,
        id: 'myFocusPage',
        muiIcon: (
            <MuiIcon icon={'filter_center_focus'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />
        ),
        permissions: 'FocusPage',
    },
    {
        ...executionConfig.statusSummary,
        id: 'StatusSummary',
        muiIcon: <MuiIcon icon={'tour'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'ProjectSummary',
    },
    {
        ...executionConfig.battlecards,
        link: executionConfig.battlecards.link,
        id: 'Battlecards',
        muiIcon: <MuiIcon icon={'checklist'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'BattleCards',
    },

    {
        ...executionConfig.progressOverview,
        id: 'ProgressOverview',
        muiIcon: <MuiIcon icon={'moving'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'ProgressOverview',
    },
    {
        ...executionConfig.completionPlan,
        id: 'completionPlan',
        muiIcon: <MuiIcon icon={'pending_actions'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'CompletionPlan',
    },

    {
        ...executionConfig.dataStories,
        id: 'DataStories',
        muiIcon: <MuiIcon icon={'collections'} type={'outlined'} fontSize={'2.3rem'} color={'var(--text-primary)'} />,
        permissions: 'DataStoryRead',
    },
    {
        ...executionConfig.pinboards,
        id: 'CustomPinboard',
        muiIcon: <MuiIcon icon={'dashboard'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'PinboardsView',
    },
];

const ProjectRoutes = () => {
    const { ability } = useUserHook();
    const { path } = useRouteMatch();
    const redirect = sideMenu.find((item) => {
        return item.permissions && ability.can('view', item.permissions);
    });

    return (
        <Responsive sideMenu={sideMenu} title={'Execution'}>
            <Switch>
                <GuardedRoute
                    path={`${path}/${executionConfig.pinboards.link}/:dashboardId?`}
                    component={PinboardsPage}
                    auth={ability.can('view', 'PinboardsView')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.dataStories.link}/:projectId?/:contractId?`}
                    component={RenderProjectDataStories}
                    auth={ability.can('view', 'DataStoryRead')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.battlecards.link}/${executionConfig.editBulkActionRules.link}`}
                    component={EditBulkActionRulesPage}
                    auth={ability.can('view', 'BattleCards')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.battlecards.link}/:id([0-9-]+)`}
                    component={ProjectActivitiesPage}
                    auth={ability.can('view', 'BattleCards')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.battlecards.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.battlecards.link}`}
                            title={executionConfig.battlecards.title}
                        />
                    )}
                    auth={ability.can('view', 'BattleCards')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.progressOverview.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={ProgressPage}
                    auth={ability.can('view', 'ProgressOverview')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.progressOverview.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.progressOverview.link}`}
                            title={executionConfig.progressOverview.title}
                        />
                    )}
                    auth={ability.can('view', 'ProgressOverview')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.statusSummary.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={ProjectStatusPage}
                    auth={ability.can('view', 'ProjectSummary')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.statusSummary.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.statusSummary.link}`}
                            title={executionConfig.statusSummary.title}
                        />
                    )}
                    auth={ability.can('view', 'ProjectSummary')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.myFocusPage.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={MyFocusPage}
                    auth={ability.can('view', 'FocusPage')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.myFocusPage.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.myFocusPage.link}`}
                            title={executionConfig.myFocusPage.title}
                        />
                    )}
                    auth={ability.can('view', 'FocusPage')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.completionPlan.link}/:id([0-9-]+)`}
                    component={CompletionPlanPage}
                    auth={ability.can('view', 'CompletionPlan')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.completionPlan.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.completionPlan.link}`}
                            title={executionConfig.completionPlan.title}
                        />
                    )}
                    auth={ability.can('view', 'CompletionPlan')}
                    redirect={`${path}/no-access`}
                />

                <Route exact path={`${path}/no-access`} component={NoAccess} />

                {/* default route */}
                <Route
                    exact
                    path={path}
                    render={() => {
                        return <Redirect to={`${path}/${redirect?.link}`} />;
                    }}
                />
                <Route path={'*'} component={PageNotFound} />
            </Switch>
        </Responsive>
    );
};
export default ProjectRoutes;
